// API Service Layer for ezPICRA Client
import config from '../config/config.jsx';
import FakeDataService from './fakeData';
import { getToken } from '../auth/session';
import http, { API_BASE_URL } from './http';
import { logger } from '../utils/logger';

class ApiService {
  constructor() {
    // In development, use the full API URL to ensure correct routing
    // In production, use the full base URL
    this.baseURL = API_BASE_URL;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
    
    // Use fake data service only when explicitly enabled
    if (config.useFakeData) {
      this.fakeService = new FakeDataService();
      console.log('🔧 Using fake data service');
    }
  }

  // Helper method to get auth headers
  getAuthHeaders() {
    const token = getToken();
    return token ? { ...this.defaultHeaders, 'Authorization': `Bearer ${token}` } : { ...this.defaultHeaders };
  }

  // Generic request method — all callers share the same axios client and 401 handling.
  async request(endpoint, options = {}) {
    let data = options.body;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        // leave as string
      }
    }

    try {
      logger.debug(`API ${options.method || 'GET'} ${endpoint}`);
      const response = await http.request({
        url: endpoint,
        method: (options.method || 'GET').toLowerCase(),
        data,
        headers: options.headers,
        responseType: options.responseType,
        params: options.params,
      });
      return response.data;
    } catch (error) {
      const status = error.response?.status;
      const errorData = error.response?.data || {};
      if (status === 401) {
        throw new Error(errorData.error || 'Authentication required');
      }
      if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
        throw new Error(
          `Network error: Cannot connect to server at ${this.baseURL}. Please ensure the server is running.`
        );
      }
      throw new Error(errorData.message || errorData.error || error.message || `HTTP error! status: ${status}`);
    }
  }

  // Health check
  async healthCheck() {
    if (config.useFakeData) {
      return this.fakeService.healthCheck();
    }
    return this.request('/health');
  }

  // Authentication endpoints
  async sendOTP(email, type = 'login') {
    if (config.useFakeData) {
      return this.fakeService.sendOTP(email, type);
    }
    return this.request('/api/auth/send-otp', {
      method: 'POST',
      body: JSON.stringify({ email, type }),
    });
  }

  async verifyOTP(email, otp, type = 'login') {
    if (config.useFakeData) {
      return this.fakeService.verifyOTP(email, otp, type);
    }
    return this.request('/api/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ email, otp, type }),
    });
  }

  // New customer signup flow
  async sendSignupOTP(email) {
    return this.request('/api/auth/send-otp', {
      method: 'POST',
      body: JSON.stringify({ email, type: 'registration' })
    });
  }

  async verifySignupOTP({ email, code, fullName }) {
    return this.request('/api/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ email, otp: code, type: 'registration', fullName })
    });
  }

  async submitSignupIntake(payload, token) {
    return this.request('/api/intake/submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });
  }

  async uploadSignupDocument({ intakeId, docType, file }, token) {
    const formData = new FormData();
    formData.append('intakeId', intakeId);
    formData.append('docType', docType);
    formData.append('file', file);
    return this.request('/api/intake/upload', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      body: formData
    });
  }

  async completeSignupDocuments({ intakeId, documents }, token) {
    return this.request('/api/documents/complete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ intakeId, documents })
    });
  }

  async getOTPStatus(email, type = 'login') {
    // Only available in development mode
    console.log('🔍 getOTPStatus - Environment check:', {
      NODE_ENV: process.env.NODE_ENV,
      hostname: window.location.hostname,
      checkResult: process.env.NODE_ENV !== 'development' && window.location.hostname !== 'localhost'
    });
    if (process.env.NODE_ENV !== 'development' && window.location.hostname !== 'localhost') {
      console.log('⚠️ getOTPStatus - Not in dev mode, returning early');
      return { hasActiveOTP: false, message: 'Only available in development mode' };
    }
    console.log('🔑 getOTPStatus - Making request to:', `/api/otp/status/${encodeURIComponent(email)}?type=${type}`);
    return this.request(`/api/otp/status/${encodeURIComponent(email)}?type=${type}`);
  }

  // User endpoints
  async createUser(userData) {
    if (config.useFakeData) {
      return this.fakeService.createUser(userData);
    }
    return this.request('/api/users/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async register(userData) {
    if (config.useFakeData) {
      return this.fakeService.createUser(userData);
    }
    return this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async googleAuth(idToken) {
    if (config.useFakeData) {
      return this.fakeService.googleAuth({ idToken });
    }
    return this.request('/api/auth/google', {
      method: 'POST',
      body: JSON.stringify({ idToken }),
    });
  }

  async getCurrentUser() {
    if (config.useFakeData) {
      return { success: true, user: await this.fakeService.getUserProfile() };
    }
    return this.request('/api/auth/me');
  }

  async getUserStats() {
    if (config.useFakeData) {
      return this.fakeService.getUserStats();
    }
    return this.request('/api/users/stats');
  }

  async getUsersByType(type, params = {}) {
    if (config.useFakeData) {
      return this.fakeService.getUsersByType(type, params);
    }
    const queryParams = new URLSearchParams(params).toString();
    return this.request(`/api/users/by-type/${type}?${queryParams}`);
  }

  async getUserProfile() {
    if (config.useFakeData) {
      return this.fakeService.getUserProfile();
    }
    return this.request('/api/users/profile');
  }

  async updateUserProfile(userData) {
    if (config.useFakeData) {
      return this.fakeService.updateUserProfile(userData);
    }
    return this.request('/api/users/profile', {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async getUsers() {
    if (config.useFakeData) {
      return this.fakeService.getUsers();
    }
    return this.request('/api/users');
  }

  async updateUser(userId, userData) {
    if (config.useFakeData) {
      return this.fakeService.updateUserProfile(userData);
    }
    return this.request(`/api/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(userId) {
    if (config.useFakeData) {
      return this.fakeService.deleteUser(userId);
    }
    return this.request(`/api/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // Project endpoints
  async getProjects() {
    if (config.useFakeData) {
      return this.fakeService.getProjects();
    }
    return this.request('/api/projects');
  }

  async getProject(id) {
    if (config.useFakeData) {
      return this.fakeService.getProject(id);
    }
    return this.request(`/api/projects/${id}`);
  }

  async createProject(projectData) {
    if (config.useFakeData) {
      return this.fakeService.createProject(projectData);
    }
    return this.request('/api/projects', {
      method: 'POST',
      body: JSON.stringify(projectData),
    });
  }

  async updateProject(id, projectData) {
    if (config.useFakeData) {
      return this.fakeService.updateProject(id, projectData);
    }
    return this.request(`/api/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(projectData),
    });
  }

  async deleteProject(id) {
    if (config.useFakeData) {
      return this.fakeService.deleteProject(id);
    }
    return this.request(`/api/projects/${id}`, {
      method: 'DELETE',
    });
  }

  // Report endpoints
  async getReports() {
    if (config.useFakeData) {
      return this.fakeService.getReports();
    }
    return this.request('/api/reports');
  }

  async getReport(id) {
    if (config.useFakeData) {
      return this.fakeService.getReport(id);
    }
    return this.request(`/api/reports/${id}`);
  }

  async createReport(reportData) {
    if (config.useFakeData) {
      return this.fakeService.createReport(reportData);
    }
    return this.request('/api/reports', {
      method: 'POST',
      body: JSON.stringify(reportData),
    });
  }

  async updateReport(id, reportData) {
    if (config.useFakeData) {
      return this.fakeService.updateReport(id, reportData);
    }
    return this.request(`/api/reports/${id}`, {
      method: 'PUT',
      body: JSON.stringify(reportData),
    });
  }

  async deleteReport(id) {
    if (config.useFakeData) {
      return this.fakeService.deleteReport(id);
    }
    return this.request(`/api/reports/${id}`, {
      method: 'DELETE',
    });
  }

  // File upload endpoint
  async uploadFile(file, type = 'report') {
    if (config.useFakeData) {
      return this.fakeService.uploadFile(file, type);
    }
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);

    return this.request('/api/reports/upload', {
      method: 'POST',
      // Don't override headers - let request() merge with auth headers
      body: formData,
    });
  }

  // File upload with metadata endpoint
  async uploadFileWithMetadata(formData) {
    if (config.useFakeData) {
      return this.fakeService.uploadFileWithMetadata(formData);
    }

    return this.request('/api/reports/upload', {
      method: 'POST',
      // Don't override headers - let request() merge with auth headers
      body: formData,
    });
  }

  // PICRA Processing endpoints
  async uploadPICRAForProcessing(formData) {
    if (config.useFakeData) {
      return this.fakeService.uploadPICRAForProcessing(formData);
    }

    return this.request('/api/picra-processing/upload', {
      method: 'POST',
      // Don't override headers - let request() merge with auth headers
      body: formData,
    });
  }

  // Extract PICRA directly from PDF using OpenAI (bypasses server-side validation)
  async extractPicraFromPDF(file) {
    if (config.useFakeData) {
      return this.fakeService.extractPicraFromPDF(file);
    }

    const formData = new FormData();
    formData.append('file', file);

    return this.request('/api/chatgpt/picra-from-pdf', {
      method: 'POST',
      body: formData,
    });
  }

  async getPICRAProcessingStatus(processingId) {
    if (config.useFakeData) {
      return this.fakeService.getPICRAProcessingStatus(processingId);
    }
    return this.request(`/api/picra-processing/status/${processingId}`);
  }

  async getPICRAProcessingResults(processingId) {
    if (config.useFakeData) {
      return this.fakeService.getPICRAProcessingResults(processingId);
    }
    return this.request(`/api/picra-processing/results/${processingId}`);
  }

  async getUserPICRAProcessingRecords(page = 1, limit = 10) {
    if (config.useFakeData) {
      return this.fakeService.getUserPICRAProcessingRecords(page, limit);
    }
    return this.request(`/api/picra-processing/user?page=${page}&limit=${limit}`);
  }

  async getProjectPICRAProcessingRecords(projectId, page = 1, limit = 10) {
    if (config.useFakeData) {
      return this.fakeService.getProjectPICRAProcessingRecords(projectId, page, limit);
    }
    return this.request(`/api/picra-processing/project/${projectId}?page=${page}&limit=${limit}`);
  }

  async retryPICRAProcessing(processingId) {
    if (config.useFakeData) {
      return this.fakeService.retryPICRAProcessing(processingId);
    }
    return this.request(`/api/picra-processing/retry/${processingId}`, {
      method: 'POST',
    });
  }

  async deletePICRAProcessingRecord(processingId) {
    if (config.useFakeData) {
      return this.fakeService.deletePICRAProcessingRecord(processingId);
    }
    return this.request(`/api/picra-processing/${processingId}`, {
      method: 'DELETE',
    });
  }

  async getPICRAProcessingStats() {
    if (config.useFakeData) {
      return this.fakeService.getPICRAProcessingStats();
    }
    return this.request('/api/picra-processing/stats');
  }

  // Quote Management
  async getQuotes(params = {}) {
    if (config.useFakeData) {
      return this.fakeService.getQuotes(params);
    }
    return this.request(`/api/quotes?${new URLSearchParams(params).toString()}`);
  }

  async getQuote(quoteId) {
    if (config.useFakeData) {
      return this.fakeService.getQuote(quoteId);
    }
    return this.request(`/api/quotes/${quoteId}`);
  }

  async createQuote(quoteData) {
    if (config.useFakeData) {
      return this.fakeService.createQuote(quoteData);
    }
    return this.request('/api/quotes', {
      method: 'POST',
      body: JSON.stringify(quoteData)
    });
  }

  async updateQuote(quoteId, quoteData) {
    if (config.useFakeData) {
      return this.fakeService.updateQuote(quoteId, quoteData);
    }
    return this.request(`/api/quotes/${quoteId}`, {
      method: 'PUT',
      body: JSON.stringify(quoteData)
    });
  }

  async sendQuote(quoteId) {
    if (config.useFakeData) {
      return this.fakeService.sendQuote(quoteId);
    }
    return this.request(`/api/quotes/${quoteId}/send`, {
      method: 'POST'
    });
  }

  async updateQuoteApproval(quoteId, status, customerResponse = null, customerNotes = null) {
    if (config.useFakeData) {
      return this.fakeService.updateQuoteApproval(quoteId, status, customerResponse, customerNotes);
    }
    return this.request(`/api/quotes/${quoteId}/approval`, {
      method: 'POST',
      body: JSON.stringify({
        status,
        customerResponse,
        customerNotes
      })
    });
  }

  async getPendingApprovalQuotes() {
    if (config.useFakeData) {
      return this.fakeService.getPendingApprovalQuotes();
    }
    return this.request('/api/quotes/pending-approval');
  }

  async addQuoteCommunication(quoteId, communicationData) {
    if (config.useFakeData) {
      return this.fakeService.addQuoteCommunication(quoteId, communicationData);
    }
    return this.request(`/api/quotes/${quoteId}/communication`, {
      method: 'POST',
      body: JSON.stringify(communicationData)
    });
  }

  async deleteQuote(quoteId) {
    if (config.useFakeData) {
      return this.fakeService.deleteQuote(quoteId);
    }
    return this.request(`/api/quotes/${quoteId}`, {
      method: 'DELETE'
    });
  }

  async getQuoteStats() {
    if (config.useFakeData) {
      return this.fakeService.getQuoteStats();
    }
    return this.request('/api/quotes/stats/overview');
  }

  // ChatGPT Integration
  async extractRepairItemsWithChatGPT(prompt, extractedText) {
    if (config.useFakeData) {
      // Simulate ChatGPT response for testing
      return new Promise(resolve => {
        setTimeout(() => {
          resolve({
            success: true,
            response: JSON.stringify([
              {
                category: "HVAC System",
                issue: "Crawlspace Ductwork (10.7.4)",
                action: "Have licensed HVAC contractor secure and support sagging return and branch ductwork",
                location: "crawlspace",
                priority: "Medium",
                estimated_cost: null
              },
              {
                category: "Plumbing",
                issue: "Leaks (11.8.2 & 11.9.2)",
                action: "Have licensed plumber repair active leak on faucet valve/water lines",
                location: "second-floor hall bathroom",
                priority: "High",
                estimated_cost: null
              }
            ])
          });
        }, 2000);
      });
    }
    
    return this.request('/api/chatgpt/extract-repair-items', {
      method: 'POST',
      body: JSON.stringify({
        prompt,
        extractedText
      })
    });
  }

  // ChatGPT / OpenAI health
  async getChatGptHealth() {
    if (config.useFakeData) {
      return {
        service: 'ChatGPT',
        status: 'healthy',
        timestamp: new Date().toISOString(),
        apiKeyConfigured: true,
        model: 'gpt-4'
      };
    }
    return this.request('/api/chatgpt/health');
  }

  // Combined PICRA extraction + repair-list using OpenAI
  async extractPicraCombined(extractedText) {
    if (config.useFakeData) {
      // Return a simple fake response for development/testing
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            success: true,
            response: `{
  "customer": {
    "customerId": null,
    "firstName": "Test",
    "lastName": "Buyer",
    "company": null,
    "email": null,
    "phone": null,
    "address": { "full": "123 Test St" },
    "lastServiceDate": null,
    "serviceType": "picra repairs",
    "status": "active",
    "nextFollowUpDate": null,
    "appSheetId": null,
    "relatedLeads": null,
    "source": "manual",
    "notes": "Fake data for development.",
    "createdAt": null,
    "updatedAt": null
  },
  "estimate": {
    "estimateId": null,
    "estimateNumber": null,
    "customer": null,
    "title": "PICRA Repair Scope",
    "description": "Fake estimate for development.",
    "propertyAddress": "123 Test St",
    "clientAddress": null,
    "lineItems": [],
    "subtotal": null,
    "tax": null,
    "taxRate": null,
    "totalAmount": null,
    "status": "Draft",
    "validUntil": null,
    "appSheetId": null,
    "leadId": null,
    "relatedContracts": null,
    "source": "manual",
    "notes": null,
    "createdAt": null,
    "updatedAt": null
  }
}

| Trade | Description | Quantity | Unit Rough Estimate | Line Rough Total | Trade Notes |
| --- | --- | --- | --- | --- | --- |
| General Contractor | Example repair item | 1 | 500 | 500 | Rough example only |

Per-Trade Subtotals (Rough):
- General Contractor: 500

Grand Total Rough Estimate: 500`
          });
        }, 1000);
      });
    }

    return this.request('/api/chatgpt/picra-combined', {
      method: 'POST',
      body: JSON.stringify({ extractedText })
    });
  }

  // MLS Property Listings
  async getPendingProperties() {
    if (config.useFakeData) {
      // Simulate property data for testing
      return new Promise(resolve => {
        setTimeout(() => {
          resolve([
            {
              property_id: '1',
              address: {
                line: '123 Main Street',
                city: 'Hampton',
                state_code: 'VA'
              },
              price: 250000,
              beds: 3,
              baths: 2,
              building_size: { size: 1500 },
              photo: 'https://via.placeholder.com/400x300',
              rdc_web_url: 'https://example.com'
            }
          ]);
        }, 1000);
      });
    }
    return this.request('/api/mls/pending');
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;

// Export the class for testing purposes
export { ApiService }; 
